import React from "react";
import {
  DatePicker,
  Input,
  TimePicker,
  Tooltip,
} from "antd";
import { ChantInput } from "../../../../../CommonComponents";
import ChantSelect from "../../../../../CommonComponents/ChantSelect/ChantSelect";
import FUpload from "../../../../commons/formFields/FUpload";
import CreateTicketDisplay from "./CreateTicketDisplay";
import dayjs from "dayjs";
import { timeFormat } from "./constants";

const EventDetailsSection = ({
  type,
  checkMatchRequired,
  matchId,
  matchList,
  loadingMatches,
  editMode,
  quantity,
  remainingTickets,
  eventName,
  section,
  location,
  allLocations,
  eventDate,
  saleEndDate,
  eventDescription,
  receiptDescription,
  layout2,
  imageFile,
  imageList,
  ticketDeliveryMethodApp,
  ticketDeliveryMethodWeb,
  refundTerms,
  contactEmail,
  groupTerms,
  stripeAddress,
  processingFee,
  processingFeeTypes,
  ticketTypes,
  showForMembers,
  showForFans,
  showOnWeb,
  currentGroup,
  routerParamsGroup,
  handleOptionChange,
  handleInputChange,
  handleDateChange,
  handleImageChange,
  uploadAction,
  checkChange,
  setLocation,
}) => {
  // Build match options for ChantSelect
  const matchOptions = (matchList || [])
    .filter((m) => new Date(m.event_date).getTime() > Date.now())
    .map((m) => ({
      value: m.fixture_id,
      label: `${m.homeTeam.team_name} vs ${m.awayTeam.team_name} (${dayjs(m.event_date).format("Do MMM, YYYY")})`,
    }));

  const matchPlaceholder = loadingMatches
    ? "Loading Matches..."
    : matchList.length === 0
      ? "No Matches"
      : "--Select Match--";

  const locationOptions = (allLocations || []).map((loc) => ({
    value: loc.id,
    label: loc.name,
  }));

  const processingFeeOptions = (processingFeeTypes || []).map((fee) => ({
    value: fee,
    label: fee,
  }));

  const ticketTypeOptions = (ticketTypes || []).map((t) => ({
    value: t,
    label: t,
  }));

  return (
    <>
      {/* ── Event Setup ── */}
      <div className="ct-section">
        <h3 className="ct-section-title">Event Setup</h3>
        <div className="ct-field-grid">
          <div className="ct-field">
            <ChantSelect
              label="Ticket Type"
              required={currentGroup !== routerParamsGroup}
              disabled={currentGroup === routerParamsGroup}
              value={type || undefined}
              onChange={(value) =>
                handleOptionChange({ target: { name: "type", value } })
              }
              options={ticketTypeOptions}
              placeholder="--Select Ticket Type--"
            />
          </div>

          {checkMatchRequired && (
            <div className="ct-field">
              <ChantSelect
                label="Match"
                required={!editMode || (editMode && quantity === remainingTickets)}
                disabled={editMode && Number(quantity) !== Number(remainingTickets)}
                value={matchId || undefined}
                onChange={(value) =>
                  handleOptionChange({ target: { name: "matchId", value } })
                }
                options={matchOptions}
                placeholder={matchPlaceholder}
                showSearch
                helpTooltip={
                  currentGroup == routerParamsGroup ? (
                    <Tooltip title="If changed, then you have to notify all the people who bought the ticket.">
                      <i className="fa fa-question-circle-o" />
                    </Tooltip>
                  ) : null
                }
              />
            </div>
          )}

          {!checkMatchRequired && type !== "Tailgate Season Pass" && (
            <div className="ct-field">
              <ChantSelect
                label="Match (Optional)"
                disabled={editMode && quantity !== remainingTickets}
                value={matchId || undefined}
                onChange={(value) =>
                  handleOptionChange({ target: { name: "matchId", value } })
                }
                options={matchOptions}
                placeholder={matchPlaceholder}
                showSearch
              />
            </div>
          )}

          {type !== "Group Match" && (
            <div className="ct-field">
              <ChantInput
                label="Event Name"
                value={eventName}
                onChange={handleInputChange}
                name="eventName"
                placeholder="Enter event name"
                required={type !== "Group Match"}
              />
            </div>
          )}

          {checkMatchRequired && type === "Group Match" && (
            <div className="ct-field">
              <ChantInput
                label="Event Name"
                value={section}
                onChange={handleInputChange}
                name="section"
                placeholder="Ex. Match ticket or Section 204"
                required
                helpText="Enter single section or range. If sections have different prices enter each as a separate item."
              />
            </div>
          )}

          {type !== "Tailgate Season Pass" && type !== "Group Match" && (
            <div className="ct-field">
              <ChantSelect
                label="Event Location"
                value={location.id || undefined}
                onChange={(value) => {
                  if (value) {
                    let selectedLocation = allLocations.find(
                      (loc) => loc.id === value,
                    );
                    setLocation(selectedLocation);
                  } else {
                    setLocation({});
                  }
                }}
                options={locationOptions}
                placeholder="--Select Location--"
                allowClear
                helpTooltip={
                  <Tooltip title="List location of event (or city)">
                    <i className="fa fa-question-circle-o" />
                  </Tooltip>
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Date & Time ── */}
      <div className="ct-section">
        <h3 className="ct-section-title">Date & Time</h3>
        <div className="ct-field-grid">
          {type !== "Tailgate Season Pass" && type !== "Group Match" && (
            <>
              <div className="ct-field">
                <label className="ct-field-label">
                  Event Date <span className="required">*</span>
                </label>
                <DatePicker
                  tabIndex="2"
                  value={eventDate}
                  onChange={(value) => handleDateChange("eventDate", value)}
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                  style={{ width: "100%" }}
                />
              </div>
              <div className="ct-field">
                <label className="ct-field-label">Event Time</label>
                <TimePicker
                  tabIndex="2"
                  value={eventDate}
                  defaultOpenValue={dayjs().set({ hour: 0, minute: 0 })}
                  onChange={(value) => handleDateChange("eventTime", value)}
                  format={timeFormat}
                  style={{ width: "100%" }}
                />
              </div>
            </>
          )}
          <div className="ct-field">
            <label className="ct-field-label">
              Sale End Date <span className="required">*</span>
              <Tooltip
                placement="topLeft"
                title="(Optional) Use as a way to end ticket sales before the event date."
              >
                <i className="fa fa-question-circle-o" style={{ color: "#888" }} />
              </Tooltip>
            </label>
            <DatePicker
              tabIndex="2"
              value={saleEndDate}
              onChange={(value) => handleDateChange("saleEndDate", value)}
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              style={{ width: "100%" }}
            />
          </div>
          <div className="ct-field">
            <label className="ct-field-label">
              Sale End Time <span className="required">*</span>
            </label>
            <TimePicker
              tabIndex="2"
              value={saleEndDate}
              defaultOpenValue={dayjs().set({ hour: 0, minute: 0 })}
              onChange={(value) => handleDateChange("saleEndTime", value)}
              format={timeFormat}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>

      {/* ── Event Details ── */}
      <div className="ct-section">
        <h3 className="ct-section-title">Event Details</h3>
        <div className="ct-field-grid">
          <div className="ct-field ct-field">
            <label className="ct-field-label">Event Description</label>
            <Input.TextArea
              tabIndex="2"
              placeholder="Add event description and ticket distribution details."
              value={eventDescription}
              name="eventDescription"
              onChange={handleInputChange}
              rows={layout2 ? 13 : 3}
            />
          </div>

          <div className="ct-field ct-field">
            <label className="ct-field-label">Description (for receipt)</label>
            <Input.TextArea
              tabIndex="2"
              placeholder="(optional) description included in stripe receipt."
              value={receiptDescription}
              name="receiptDescription"
              maxLength={500}
              onChange={handleInputChange}
              rows={layout2 ? 13 : 3}
            />
          </div>

          {(!matchId || matchId?.length === 0) && (
            <div className="ct-field ct-field">
              <FUpload
                action={uploadAction}
                fileList={imageList}
                onChange={handleImageChange}
                label="Image"
                aspectRatio={1}
                tooltip={() => (
                  <div style={{ width: 400, fontSize: 16 }}>
                    <span>
                      Image required (1:1 ratio) for special events and
                      tailgate season passes.
                      <br /> Not displayed on ticket but on callouts/shortcuts
                      to ticket.
                      <br /> For events not associated with a match the image
                      is used in place of the opponent logo.
                      <br /> Image not required for events associated with a
                      match.
                    </span>
                  </div>
                )}
                required
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Delivery & Terms ── */}
      <div className="ct-section">
        <h3 className="ct-section-title">Delivery & Contact</h3>
        <div className="ct-field-grid">
          <div className="ct-field">
            <ChantInput
              label="Ticket Delivery Method (App)"
              value={ticketDeliveryMethodApp}
              onChange={handleInputChange}
              name="ticketDeliveryMethodApp"
              placeholder="e.g. QR code, via email, show at door"
              maxLength={150}
              helpText="How tickets are delivered when purchased in the app."
            />
          </div>

          <div className="ct-field">
            <ChantInput
              label="Ticket Delivery Method (Web)"
              value={ticketDeliveryMethodWeb}
              onChange={handleInputChange}
              name="ticketDeliveryMethodWeb"
              placeholder="e.g. Email receipt will serve as your ticket"
              maxLength={150}
              helpText="How tickets are delivered when purchased via web."
            />
          </div>

          <div className="ct-field">
            <ChantInput
              label="Refund Terms"
              value={refundTerms}
              onChange={handleInputChange}
              name="refundTerms"
              placeholder="Enter refund terms"
              maxLength={150}
            />
          </div>

          <div className="ct-field">
            <ChantInput
              label="Contact Email"
              value={contactEmail}
              onChange={handleInputChange}
              name="contactEmail"
              placeholder="support@yourgroup.com"
              required
            />
          </div>

          <div className="ct-field">
            <ChantInput
              label="Group Terms"
              value={groupTerms}
              onChange={handleInputChange}
              name="groupTerms"
              placeholder="(optional) URL if group terms must be accepted"
            />
          </div>
        </div>
      </div>

      {/* ── Payment & Display ── */}
      <div className="ct-section">
        <h3 className="ct-section-title">Payment & Display</h3>
        <div className="ct-field-grid">
          <div className="ct-field">
            <label className="ct-field-label">Stripe Merchant Account</label>
            <div className={`ct-stripe-status ${!stripeAddress ? "inactive" : ""}`}>
              {stripeAddress
                ? "Active"
                : "Requires setup. Contact support@chant.fan."}
            </div>
          </div>

          <div className="ct-field">
            <ChantSelect
              label="Processing Fee"
              required
              value={processingFee || undefined}
              onChange={(value) =>
                handleOptionChange({ target: { name: "processingFee", value } })
              }
              options={processingFeeOptions}
              placeholder="--Select--"
              helpText="Stripe (2.9% + $0.30) + Chant (3%)"
              helpTooltip={
                <Tooltip
                  placement="topLeft"
                  title={
                    <div>
                      <p style={{ fontWeight: "bold" }}>Fees</p>
                      <p style={{ marginBottom: 0 }}>
                        Stripe card processing fee: 2.9% + $0.30 (can vary)
                      </p>
                      <p>Chant fee: 3%</p>
                      <p style={{ fontWeight: "bold" }}>Member Pay</p>
                      <p>
                        Stripe collects the fee on the entire purchase amount.
                        To account for this we add 3% + $.30 to the original
                        item price so your net is close to the item price.
                      </p>
                      <p style={{ fontWeight: "bold" }}>
                        Member Pay (legal limitations)
                      </p>
                      <p>
                        Not all states/countries allow the fee to be passed to
                        the member. Please confirm for your location.
                      </p>
                    </div>
                  }
                >
                  <i className="fa fa-question-circle-o" />
                </Tooltip>
              }
            />
          </div>

          <div className="ct-field ct-field">
            <CreateTicketDisplay
              showForMembers={showForMembers}
              showForFans={showForFans}
              showOnWeb={showOnWeb}
              checkChange={checkChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EventDetailsSection;
